import type {
  ImportComputation,
  ImportPreview,
  MappingProposalEntry,
  ReimportCandidate,
  ReimportDecisions,
  SheetPrefill,
  SheetType,
  StructurePrefill,
  UserCodeEntry,
  UserDirectoryEntry,
  UserMapAssignment,
} from '@testflow/shared';
import { ApiError, BASE, assertAuthorized, request } from './api.js';

export interface ImportJob {
  import_job_id: string;
  filename: string;
  status: 'upload' | 'classified' | 'mapped' | 'committed' | 'aborted';
  stream_name?: string | null;
  teilprojekt_name?: string | null;
  // #125 (Q5): „Neuimport von" — der frühere Import, gegen den dieser Job abgleicht.
  reimport_of_job_id?: string | null;
}

export interface ImportSheet {
  sheet_id: string;
  sheet_index: number;
  sheet_name: string;
  detected_type: SheetType | null;
  confidence: number | null; // Prozentpunkte 0..100
  reason: string | null;
  classification_source: string | null; // 'ai' | 'heuristic'
  confirmed_type: SheetType | null;
  headers: string[];
  mapping: Record<string, string> | null;
  row_count?: number;
  discard_count?: number;
  // Task 11 (#67): "Blatt ignorieren" — persistiert unabhängig vom Typ-Bestätigungs-Schritt, siehe
  // apps/api/src/routes/import.ts PATCH .../sheets/:sheetId (Task 8). Optional wie row_count/
  // discard_count, weil ältere Fixtures/Aufrufer das Feld nicht mitgeben; das Backend liefert es
  // aber immer.
  excluded?: boolean;
  // #72: erkannte Quellsprache (KI/Heuristik), `null` solange unbekannt. Wie detected_type nur ein
  // Vorschlag — der Mensch korrigiert ihn über setSheetLanguage unten, wenn die Erkennung daneben lag.
  detected_language?: 'de' | 'en' | null;
  // #125 (Vorbelegung beim Neuimport): abgeleitet vom Server — 'applied' = Typ, Sprache, Ignorieren
  // und Mapping stammen (noch) aus dem gleichnamigen Blatt des Quell-Imports; 'headers_differ' =
  // gleichnamiges Blatt mit anderen Spalten, nichts übernommen. Fehlt bei Neuanlage.
  prefill?: SheetPrefill | null;
}

export interface MappingProposal {
  entries: MappingProposalEntry[];
  profile_hit: boolean;
  profile_columns: number;
  // #125: Dateiname des Quell-Imports, wenn das Mapping von dort übernommen ist.
  prefilled_from?: string | null;
}

export interface DiscardedRow {
  sheet_name: string;
  row_no: number;
  cells: Record<string, string>;
  reason: string | null;
}

export interface CommitResult {
  processes: number;
  test_cases: number;
  steps: number;
  e2e: number;
  first_human_id: string | null;
  // #125: nur beim Neuimport ≠ 0 — geänderte, auf nicht_im_scope gesetzte und gelöschte Testfälle,
  // geänderte Ketten und Kettenglieder, deren Testfall nicht geschrieben wurde (abgewählt).
  updated: number;
  dropped: number;
  deleted: number;
  e2e_updated: number;
  unresolved_members: number;
  // #125: neue Ketten, die wegen eines abgewählten eigenen neuen Glieds NICHT angelegt wurden.
  chains_skipped_incomplete: Array<{ key: string; name: string }>;
  // #125: neue Testfälle, die mit ihrer nicht angelegten neuen Kette zurückgehalten wurden.
  test_cases_held_back: Array<{ key: string; title: string; chain_key: string }>;
  mode: 'insert' | 'reimport';
}

export interface StructureSaved {
  ok: boolean;
  sheets?: ImportSheet[];
  job?: { stream_name: string | null; teilprojekt_name: string | null };
  structure_prefill?: StructurePrefill | null;
}

// #73: Der Upload antwortet, bevor die Blätter erkannt sind — `computation` ist der Zustand der
// Erkennung im Hintergrund, die Blätter tragen bis dahin keinen Befund (detected_type null).
export interface UploadResult {
  job: ImportJob;
  sheets: ImportSheet[];
  computation: ImportComputation;
}

const jobsPath = (projectId: string): string => `/v1/projects/${projectId}/import/jobs`;

export const importApi = {
  // Kein Content-Type: den multipart-Boundary setzt der Browser. Deshalb NICHT über request() —
  // aber denselben Session-Vertrag (401 -> Login-Redirect) über assertAuthorized() einhalten.
  async upload(projectId: string, file: File): Promise<UploadResult> {
    const form = new FormData();
    form.append('file', file);
    const path = jobsPath(projectId);
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    assertAuthorized(res, path);
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.json() as Promise<UploadResult>;
  },

  getJob: (projectId: string, jobId: string): Promise<{ job: ImportJob; sheets: ImportSheet[] }> =>
    request(`${jobsPath(projectId)}/${jobId}`),

  confirmSheetType: (
    projectId: string,
    jobId: string,
    sheetId: string,
    sheetType: SheetType,
  ): Promise<MappingProposal> =>
    request(`${jobsPath(projectId)}/${jobId}/sheets/${sheetId}`, {
      method: 'PATCH',
      body: JSON.stringify({ sheet_type: sheetType }),
    }),

  // Task 11 (#67): "Blatt ignorieren" — unabhängig vom Typ-Bestätigungs-Schritt, derselbe
  // PATCH-Endpunkt wie confirmSheetType (Task 8, apps/api/src/routes/import.ts), aber mit
  // `excluded` statt `sheet_type` im Body, damit ein reiner Ignorieren-Toggle keinen neuen
  // Mapping-Vorschlag anstößt.
  setSheetExcluded: (
    projectId: string,
    jobId: string,
    sheetId: string,
    excluded: boolean,
  ): Promise<{ ok: boolean }> =>
    request(`${jobsPath(projectId)}/${jobId}/sheets/${sheetId}`, {
      method: 'PATCH',
      body: JSON.stringify({ excluded }),
    }),

  // #72: Quellsprache korrigieren — derselbe PATCH-Endpunkt wie confirmSheetType/setSheetExcluded
  // (apps/api/src/routes/import.ts), aber mit `source_language` im Body, damit ein reiner
  // Sprach-Umschalter keinen neuen Mapping-Vorschlag anstößt. Der Server verwirft dabei den
  // persistierten Preview-Draft (Sprache bestimmt die Übersetzungsrichtung).
  setSheetLanguage: (
    projectId: string,
    jobId: string,
    sheetId: string,
    language: 'de' | 'en',
  ): Promise<{ ok: boolean }> =>
    request(`${jobsPath(projectId)}/${jobId}/sheets/${sheetId}`, {
      method: 'PATCH',
      body: JSON.stringify({ source_language: language }),
    }),

  confirmMapping: (
    projectId: string,
    jobId: string,
    sheetId: string,
    mappings: Record<string, string>,
  ): Promise<{ ok: boolean }> =>
    request(`${jobsPath(projectId)}/${jobId}/sheets/${sheetId}/mapping`, {
      method: 'PUT',
      body: JSON.stringify({ mappings }),
    }),

  // Task 8 (#64): Wizard-Felder Stream/Teilprojekt — Teil-Update, je Feld unabhängig auf Blur
  // aufgerufen (siehe upload-step.tsx), deshalb sind beide Body-Felder optional.
  // #125: Wer `reimport_of_job_id` schickt, bekommt die Blätter nach der Vorbelegung zurück —
  // samt Stream/Teilprojekt (auch die können aus der Quelle übernommen sein).
  setStructure: (
    projectId: string,
    jobId: string,
    body: {
      stream_name?: string;
      teilprojekt_name?: string | null;
      reimport_of_job_id?: string | null;
      // #125 (Fix-Runde 2): vom Admin bearbeitete Struktur-Felder — übernimmt ein Quellwechsel nie.
      edited_fields?: Array<'stream_name' | 'teilprojekt_name'>;
    },
  ): Promise<StructureSaved> =>
    request(`${jobsPath(projectId)}/${jobId}/structure`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // #125 (Q5): frühere Importe als Quelle eines Neuimports, mit Vorschlag per Dateiname.
  getReimportCandidates: (
    projectId: string,
    jobId: string,
  ): Promise<{ candidates: ReimportCandidate[]; suggested_job_id: string | null }> =>
    request(`${jobsPath(projectId)}/${jobId}/reimport-candidates`),

  // #73: startet die Blatt-Erkennung erneut (nach Abbruch/Fehlschlag); läuft sie schon, kommt ihr
  // Zustand zurück. 409 import_already_classified, wenn die Blätter schon erkannt sind.
  restartClassification: (projectId: string, jobId: string): Promise<ImportComputation> =>
    request(`${jobsPath(projectId)}/${jobId}/classification`, { method: 'POST' }),

  // #73: startet die Vorschau-Berechnung im Hintergrund (202) oder liefert den fertigen Entwurf (200).
  startPreview: (projectId: string, jobId: string): Promise<ImportComputation> =>
    request(`${jobsPath(projectId)}/${jobId}/preview`, { method: 'POST' }),

  getComputation: (projectId: string, jobId: string): Promise<ImportComputation> =>
    request(`${jobsPath(projectId)}/${jobId}/computation`),

  getPreview: (projectId: string, jobId: string): Promise<ImportPreview> =>
    request(`${jobsPath(projectId)}/${jobId}/preview`),

  getDiscards: (projectId: string, jobId: string): Promise<DiscardedRow[]> =>
    request(`${jobsPath(projectId)}/${jobId}/discards`),

  // #125: der Neuimport schickt die Entscheidungen aus der Vorschau (mit Fingerabdruck) mit; der
  // Normalimport weiter ohne Body.
  commit: (
    projectId: string,
    jobId: string,
    decisions?: ReimportDecisions,
  ): Promise<CommitResult> =>
    request(`${jobsPath(projectId)}/${jobId}/commit`, {
      method: 'POST',
      ...(decisions ? { body: JSON.stringify(decisions) } : {}),
    }),

  // Task 10 (#65): Sachbearbeiter-Kürzel aus responsible_code-Spalten -> Vorschlag für die
  // Nutzerzuordnung (Task 5, /jobs/:jobId/user-codes).
  // #75 Teil 1: `users` trägt zusätzlich die volle (aktive) Kandidatenliste des Projekts für die
  // Freitextsuche — ergänzt die fuzzy Top-5 aus `codes[].matches`, falls der richtige Nutzer dort
  // nicht auftaucht.
  getUserCodes: (
    projectId: string,
    jobId: string,
  ): Promise<{ codes: UserCodeEntry[]; users: UserDirectoryEntry[] }> =>
    request(`${jobsPath(projectId)}/${jobId}/user-codes`),

  saveUserMap: (
    projectId: string,
    jobId: string,
    assignments: UserMapAssignment[],
  ): Promise<{ ok: boolean }> =>
    request(`${jobsPath(projectId)}/${jobId}/user-map`, {
      method: 'POST',
      body: JSON.stringify({ assignments }),
    }),
};
